import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { Loader2, Search, Plus, FolderOpen } from "lucide-react";

import { UnifiedModal } from "@/components/ui/unified-modal";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";

import { useToast } from "@/hooks/use-toast";
import { useCreateCollection, useWritableCollections } from "@/hooks/useCollections";
import { COLLECTION_PALETTE } from "@/constants/collections";
import { handleInputFocus } from "@/utils/keyboard";
import { cn } from "@/lib/utils";
import type { Collection } from "@/types/collections";
import { CAPTION, META, ROW_TITLE } from "@/lib/typography";

interface CollectionPickerStackedProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCollectionIds?: string[];
  onCollectionsChange?: (collectionIds: string[]) => void;
}

export function CollectionPickerStacked({
  open,
  onOpenChange,
  selectedCollectionIds = [],
  onCollectionsChange,
}: CollectionPickerStackedProps) {
  const { toast } = useToast();

  const { collections: allCollections, isLoading } = useWritableCollections();
  const createMutation = useCreateCollection();

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set(selectedCollectionIds));
  const [creatingInline, setCreatingInline] = useState(false);
  const [newTitle, setNewTitle] = useState("");

  // Ordered: selected first, then rest
  const orderedCollections = useMemo(() => {
    const selected = allCollections.filter((c) => selectedIds.has(c.collection_id));
    const unselected = allCollections.filter((c) => !selectedIds.has(c.collection_id));
    return [...selected, ...unselected];
  }, [allCollections, selectedIds]);

  // Filtered by search
  const filteredCollections = useMemo(() => {
    if (!searchQuery.trim()) return orderedCollections;
    const q = searchQuery.toLowerCase().trim();
    return orderedCollections.filter((c) => c.title.toLowerCase().includes(q));
  }, [orderedCollections, searchQuery]);

  // Check if search query would create a new collection
  const isNewName = useMemo(() => {
    const trimmed = searchQuery.trim().toLowerCase();
    return (
      trimmed.length > 0 &&
      trimmed.length <= 100 &&
      !allCollections.some((c) => c.title.toLowerCase() === trimmed)
    );
  }, [allCollections, searchQuery]);

  // Reset state when picker opens
  useEffect(() => {
    if (open) {
      setSelectedIds(new Set(selectedCollectionIds));
      setSearchQuery("");
      setCreatingInline(false);
      setNewTitle("");
    }
  }, [open]);

  const toggleCollection = (collectionId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(collectionId)) {
        next.delete(collectionId);
      } else {
        next.add(collectionId);
      }
      return next;
    });
  };

  const handleQuickCreate = () => {
    const name = searchQuery.trim();
    if (!name || createMutation.isPending) return;

    const color = COLLECTION_PALETTE[Math.floor(Math.random() * COLLECTION_PALETTE.length)];
    createMutation.mutate(
      { title: name, color },
      {
        onSuccess: (created) => {
          setSelectedIds((prev) => new Set([...prev, created.collection_id]));
          setSearchQuery("");
          toast({ title: "Collection created", description: `Created "${created.title}"` });
        },
        onError: (error: any) => {
          toast({
            title: "Error creating collection",
            description: error.message || "Please try again",
            variant: "destructive",
          });
        },
      },
    );
  };

  const handleInlineCreate = () => {
    const title = newTitle.trim();
    if (!title || createMutation.isPending) return;

    const randomColor = COLLECTION_PALETTE[Math.floor(Math.random() * COLLECTION_PALETTE.length)];
    createMutation.mutate(
      { title, color: randomColor },
      {
        onSuccess: (created) => {
          setSelectedIds((prev) => new Set([...prev, created.collection_id]));
          setCreatingInline(false);
          setNewTitle("");
          toast({ title: "Collection created", description: `Created "${created.title}"` });
        },
        onError: (error: any) => {
          toast({
            title: "Error creating collection",
            description: error.message || "Please try again",
            variant: "destructive",
          });
        },
      },
    );
  };

  const handleSave = () => {
    onCollectionsChange?.(Array.from(selectedIds));
    onOpenChange(false);
  };

  const handleCancel = () => {
    setSelectedIds(new Set(selectedCollectionIds));
    setSearchQuery("");
    onOpenChange(false);
  };

  return (
    <UnifiedModal
      open={open}
      onOpenChange={onOpenChange}
      icon={<FolderOpen />}
      title="Select Collections"
      description="Choose collections for this post."
      stacked
      contentClassName="max-w-[90vw] sm:max-w-md"
    >
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4 pt-2 pb-4">
        {/* Search / Quick Create */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search or create collections..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              autoFocus={false}
              onFocus={handleInputFocus}
              onKeyPress={(e) => {
                if (e.key === "Enter" && isNewName) {
                  handleQuickCreate();
                }
              }}
            />
          </div>
          {isNewName && (
            <Button size="sm" onClick={handleQuickCreate} disabled={createMutation.isPending} className="px-3">
              {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              <span className="ml-1">Create</span>
            </Button>
          )}
        </div>

        {/* Inline Create (expanded form with color picker) */}
        {creatingInline ? (
          <div className="rounded-xl border border-border p-3 space-y-3">
            <Input
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="Collection name"
              maxLength={100}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") handleInlineCreate();
                if (e.key === "Escape") setCreatingInline(false);
              }}
            />
            <div className="flex gap-3">
              <Button variant="secondary" size="modal" onClick={() => setCreatingInline(false)} className="flex-1">
                Cancel
              </Button>
              <Button
                variant="gradient"
                size="modal"
                onClick={handleInlineCreate}
                disabled={!newTitle.trim() || createMutation.isPending}
                className="flex-1"
              >
                {createMutation.isPending ? "Creating..." : "Create"}
              </Button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setCreatingInline(true)}
            className={cn(META, "flex w-full items-center gap-3 rounded-xl border-2 border-dashed border-border p-3 text-left transition-colors hover:border-primary hover:text-primary")}
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
              <Plus className="h-4 w-4" />
            </div>
            <span className="font-medium">New collection</span>
          </button>
        )}

        {/* Collection List */}
        <ScrollArea className="h-[280px] border rounded-xl p-3">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-6 w-6 animate-spin text-brand-accent" />
            </div>
          ) : filteredCollections.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              {searchQuery && isNewName ? (
                <p>Press Enter or click Create to add &quot;{searchQuery}&quot;</p>
              ) : searchQuery ? (
                <p>No collections matching &quot;{searchQuery}&quot;</p>
              ) : (
                <p>No collections yet. Create your first one above!</p>
              )}
            </div>
          ) : (
            <div className="space-y-1">
              {filteredCollections.map((collection) => (
                <div
                  key={collection.collection_id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer hover:bg-muted/50"
                  onClick={() => toggleCollection(collection.collection_id)}
                >
                  <Checkbox
                    checked={selectedIds.has(collection.collection_id)}
                    onCheckedChange={() => toggleCollection(collection.collection_id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <FolderOpen className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                  <div className="flex flex-1 min-w-0 items-center gap-2">
                    <span className={cn(ROW_TITLE, "truncate text-foreground")}>
                      {collection.title}
                    </span>
                    <span className={cn(CAPTION, "flex-shrink-0")}>
                      {collection.item_count} {collection.item_count === 1 ? "item" : "items"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Actions */}
        <div className="flex flex-row gap-3">
          <Button variant="secondary" size="modal" onClick={handleCancel} className="flex-1">
            Cancel
          </Button>
          <Button size="modal" onClick={handleSave} className="flex-1">
            {`Done${selectedIds.size ? ` (${selectedIds.size})` : ""}`}
          </Button>
        </div>
      </motion.div>
    </UnifiedModal>
  );
}
